import type { VariantValue } from '@libs/algolia';

import { AUTOSHIP_ONE_TIME_OPTION, DEFAULT_AUTOSHIP_CADENCE_VALUE } from '../autoship/cadence';
import type { ResponseError } from '../response-error';

export type ImageType = {
    type: 'image';
    url: string;
};

export type VideoType = {
    type: 'video';
    poster: string;
    sizes: {
        small: string;
        medium: string;
        large: string;
    };
};

export type MediaEntityType = ImageType | VideoType;

export type PersonalizationType = {
    templateId: string | number;
    templateName: string;
    customizationType: string;
    templateDescription: string;
    fontFaceOptions?: string[];
    isCustomizationMandatory?: boolean;
    styleOptions?: { threadColors?: string[]; styleFinishes?: string[] };
    textLocations?: { frontLines?: number[]; backLines?: number[] };
};

export type DeliveryMethod = {
    availablePurchaseOptions: { oneTime: boolean; autoship: boolean };
    purchaseQuantities?: { min: number; max: number };
};

export type DeliveryMethodSTH = Prettify<
    DeliveryMethod & {
        poBoxRestricted?: boolean;
        isInStock?: boolean; // For SEO purposes only, checks web inventory
    }
>;

export type SeoType = {
    title: string;
    description: string;
    canonicalUrl: string;
};

export type PharmacyType = 'rx_med' | 'rx_food';

export type ProductPharmacyData = {
    type: PharmacyType;
    coldStorage: boolean;
    oos: boolean;
    autoshipBypass: boolean;
    quantity: string;
};

export type Promotion = {
    text: string;
    order: number;
    key?: string | null;
    start?: string | null;
    end?: string | null;
    type?: string | null;
    isDefault?: boolean | null;
    discountDisplay?: string | null;
};

export type VariantKey = 'size' | 'flavor' | 'color' | 'quantity' | 'wattage';
export type VariantValues = Partial<Record<VariantKey, string>>;

// What we get from API
export type Product = {
    name: string;
    brand: Nullable<{ name: string; url: string }>;
    description: Nullable<string>;
    price: {
        original: number;
        sale: Nullable<number>;
        saleValidUntil: Nullable<string>;
    };
    priceData?: {
        original: number | undefined;
        current: number | undefined;
        sale: Nullable<number>;
    };
    media: MediaEntityType[];
    variantValues: Nullable<VariantValues>;
    rating: { average: number; count: number };
    personalization?: PersonalizationType;
    deliveryMethods: {
        STH: DeliveryMethodSTH;
        BOPIS: DeliveryMethod;
        SDD: DeliveryMethod;
    };
    autoship: {
        discountDisplay?: string;
        firstTimePrice?: number | null;
        recurringPrice?: number | null;
    };
    pharmacy: Nullable<ProductPharmacyData>;
    promotions: Nullable<Promotion[]>;
    isFrequencyCalculatorEligible: boolean;
    isLivePet: boolean;
    guestTreatPoints: number;
    upc: string;
    sizeChartImgUrl: string;
    recommendedAutoshipFrequency?: string | null;
    recommendedAutoshipFrequencyUnitOfMeasure?: string | null;
};

export type ProductApiResponse = {
    data: {
        webStyleId: string;
        defaultSku: string;
        variants: { [sku: string]: Product };
        variantToSkuMap: Nullable<Record<string, string>>;
        uniqueVariantValues: Nullable<Partial<Record<VariantKey, VariantValue[]>>>;
        primaryCategory: { id: string; name: string };
        categories: string[];
        merchandisingHierarchy: Nullable<{
            category?: string;
            class?: string;
            department?: string;
            division?: string;
            vicePresidentId?: number;
            vicePresidentName?: string;
        }>;
    };
    errors: ResponseError[];
};

export const AUTOSHIP_CONST = {
    FREQUENCY_DROPDOWN_LABEL: 'Choose Frequency',
    FREQUENCY_LABEL: 'Frequency',
    QUANTITY_DROPDOWN_LABEL: 'Quantity',
    ADD_BTN_AUTOSHIP: 'Add for autoship',
    DEFAULT_OPTION: DEFAULT_AUTOSHIP_CADENCE_VALUE,
    NOT_AVAIL_MSG: 'Not available for this item',
    AVAIL_MSG: 'Save on recurring orders',
    DRAWER_LINK_TEXT: 'Learn more',
    ADD_TO_AUTOSHIP_TEXT: 'Add to autoship',
    ADDED_TO_AUTOSHIP_TEXT: 'Added to autoship',
    ONE_TIME_LABEL: AUTOSHIP_ONE_TIME_OPTION.label,
    ONE_TIME_VALUE: AUTOSHIP_ONE_TIME_OPTION.value,
} as const;
